/*
 * pdp-stories.js — mobile (≤749px) stories-style gallery for the PDP.
 *
 * Each .product__pair (the recto/verso faces of ONE size) becomes a
 * horizontal scroll-snap track of full-width slides, with a row of thin
 * progress bars above (.product__stories-bars). On mobile this script:
 *   - auto-advances to the next face every INTERVAL ms,
 *   - fills the ACTIVE bar grey→black L→R over that interval (CSS width
 *     transition keyed off --stories-duration + .is-active),
 *   - updates the active slide + bar when the user swipes (native scroll),
 *   - jumps to a slide when its bar is tapped,
 *   - pauses while off-screen (IntersectionObserver) and only runs for the
 *     pair that is currently visible (the per-size :has() track hides the
 *     others off to the side),
 *   - under prefers-reduced-motion: NO auto-advance, bars static (the
 *     current bar stays filled), manual swipe/tap still work.
 *
 * Desktop (≥750px) keeps the side-by-side pair untouched: the bars are
 * display:none and this script no-ops (the controllers idle and the pair
 * is not a scroller there). JS-off degrades to a plain swipeable scroller
 * showing the first face.
 */
(function () {
  'use strict';

  var INTERVAL = 5000;
  var mobileMQ = window.matchMedia('(max-width: 749px)');
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  function Story(pair, bars) {
    this.pair = pair;
    this.bars = Array.prototype.slice.call(
      bars.querySelectorAll('.product__stories-bar')
    );
    this.slides = Array.prototype.slice.call(
      pair.querySelectorAll('.product__media-item')
    );
    this.index = 0;
    this.timer = null;
    this.visible = false;
    this.running = false;
    this.scrollRAF = null;

    var self = this;

    // expose the interval to CSS so the fill transition matches exactly
    pair.style.setProperty('--stories-duration', INTERVAL / 1000 + 's');

    // tapping a bar jumps to that slide
    this.bars.forEach(function (bar, i) {
      bar.addEventListener('click', function () {
        self.goTo(i, true);
        self.restart();
      });
    });

    // native swipe → keep the active bar in sync (debounced via rAF)
    pair.addEventListener(
      'scroll',
      function () {
        if (self.scrollRAF) return;
        self.scrollRAF = requestAnimationFrame(function () {
          self.scrollRAF = null;
          self.syncFromScroll();
        });
      },
      { passive: true }
    );

    this.setActive(0, false);
  }

  // which slide is centred under the current scroll position
  Story.prototype.currentScrollIndex = function () {
    if (!this.slides.length) return 0;
    var w = this.pair.clientWidth || 1;
    return Math.round(this.pair.scrollLeft / w);
  };

  Story.prototype.syncFromScroll = function () {
    // ignore the scroll events our own goTo() emits — only a USER swipe
    // should re-sync the bars and restart the dwell (programmatic scrolls
    // already set the index, and self-correcting on them caused restart
    // thrash mid smooth-scroll)
    if (this.programmatic) return;
    var i = this.currentScrollIndex();
    if (i === this.index) return;
    this.setActive(i, false);
    // a manual swipe restarts the dwell on the new slide
    this.restart();
  };

  // paint the bars: bars before i = done (instant full), bar i = active
  // (animates the fill over the interval when `animate`) or done (instant
  // full when static — reduced motion / paused), bars after i = empty.
  Story.prototype.setActive = function (i, animate) {
    this.index = i;
    this.bars.forEach(function (bar, b) {
      bar.classList.remove('is-active', 'is-done');
      if (b < i) {
        bar.classList.add('is-done');
      } else if (b === i) {
        if (animate) {
          // restart the CSS width transition from 0: empty it, force a
          // reflow, then add is-active so the fill re-runs from L→R
          bar.classList.remove('is-active');
          void bar.offsetWidth;
          bar.classList.add('is-active');
        } else {
          // static fill (initial paint / reduced motion / paused)
          bar.classList.add('is-done');
        }
      }
    });
  };

  Story.prototype.goTo = function (i, smooth) {
    if (i < 0) i = 0;
    if (i > this.slides.length - 1) i = 0; // wrap to first
    var slide = this.slides[i];
    if (!slide) return;
    var left = slide.offsetLeft - this.pair.offsetLeft;
    // mark the scroll as programmatic so syncFromScroll ignores the events
    // this triggers; cleared after the smooth-scroll settles
    this.programmatic = true;
    if (this.programmatic_t) window.clearTimeout(this.programmatic_t);
    var self = this;
    this.programmatic_t = window.setTimeout(function () {
      self.programmatic = false;
    }, 600);
    if (this.pair.scrollTo) {
      this.pair.scrollTo({
        left: left,
        behavior: smooth && !reduceMQ.matches ? 'smooth' : 'auto',
      });
    } else {
      this.pair.scrollLeft = left;
    }
    this.setActive(i, this.running);
  };

  Story.prototype.next = function () {
    this.goTo(this.index + 1, true);
  };

  Story.prototype.start = function () {
    if (this.running) return;
    if (!mobileMQ.matches) return;
    if (reduceMQ.matches) {
      // manual only: leave the current bar filled, no timer
      this.setActive(this.index, false);
      return;
    }
    if (this.slides.length < 2) return;
    this.running = true;
    // (re)trigger the active bar's fill animation for the current dwell
    this.setActive(this.index, true);
    var self = this;
    this.timer = window.setInterval(function () {
      self.next();
    }, INTERVAL);
  };

  Story.prototype.stop = function () {
    this.running = false;
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  Story.prototype.restart = function () {
    if (!this.running) return;
    this.stop();
    this.start();
  };

  Story.prototype.onVisible = function (visible) {
    this.visible = visible;
    if (visible && mobileMQ.matches) {
      this.start();
    } else {
      this.stop();
    }
  };

  function init() {
    var stories = [];
    var pairs = document.querySelectorAll('product-media .product__pair');
    Array.prototype.forEach.call(pairs, function (pair) {
      var slide = pair.closest('.product__slide');
      if (!slide) return;
      var bars = slide.querySelector('.product__stories-bars');
      if (!bars) return;
      stories.push(new Story(pair, bars));
    });
    if (!stories.length) return;

    // pause/resume per pair based on viewport visibility — only the
    // selected size's pair is on screen (others are translated aside)
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var story = entry.target.__story;
            if (story) story.onVisible(entry.isIntersecting);
          });
        },
        { threshold: 0.35 }
      );
      stories.forEach(function (story) {
        story.pair.__story = story;
        io.observe(story.pair);
      });
    } else {
      // no IO: just run the default-size pair if mobile
      stories.forEach(function (story) {
        if (mobileMQ.matches) story.start();
      });
    }

    // when the viewport crosses the mobile breakpoint, start/stop cleanly
    var onMQ = function () {
      stories.forEach(function (story) {
        if (mobileMQ.matches && story.visible) {
          story.start();
        } else {
          story.stop();
        }
      });
    };
    if (mobileMQ.addEventListener) {
      mobileMQ.addEventListener('change', onMQ);
    } else if (mobileMQ.addListener) {
      mobileMQ.addListener(onMQ);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
